/**
 * Upload a picture or a file (T7.25).
 *
 * One dialogue, opened for a given kind, because the two differ in more than a
 * label: a picture has a 2 MB cap and a fixed list of accepted types, a file has
 * a 10 MB cap and may be anything. The checks here save a round trip; the API
 * sniffs the content and is the only real enforcement.
 *
 * The heading is required, because the view renders a picture under its heading
 * and lists a file by it. The API bounds it to 200 characters and escapes it on
 * the way out; this only stops an empty one being sent.
 */
import { useRef, useState, type FormEvent } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ApiError, uploadWithProgress } from '../../lib/api';
import {
  MAX_FILE_BYTES,
  MAX_PICTURE_BYTES,
  PICTURE_MIME,
  documentKeys,
  formatSize,
  type BlueprintDocument,
  type DocumentKind,
} from '../../lib/documents';
import { Dialog } from '../versions/Dialog';

const HEADING_MAX = 200;

export function UploadDocumentDialog({
  blueprintId,
  kind,
  label,
}: {
  blueprintId: string;
  kind: DocumentKind;
  /** The button's words, so "Add a picture" and "Add a file" read naturally. */
  label: string;
}) {
  const queryClient = useQueryClient();
  const fileInput = useRef<HTMLInputElement>(null);
  const abort = useRef<AbortController | null>(null);

  const [open, setOpen] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [heading, setHeading] = useState('');
  const [sending, setSending] = useState(false);
  const [fraction, setFraction] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const picture = kind === 'picture';
  const maxBytes = picture ? MAX_PICTURE_BYTES : MAX_FILE_BYTES;

  const close = () => {
    if (sending) return;
    setOpen(false);
    setFile(null);
    setHeading('');
    setError(null);
    setFraction(0);
  };

  const choose = (picked: File | null) => {
    setError(null);
    if (!picked) {
      setFile(null);
      return;
    }
    if (picture && !(PICTURE_MIME as readonly string[]).includes(picked.type)) {
      setFile(null);
      setError('Choose a PNG, JPEG, WebP, GIF, BMP or SVG picture.');
      return;
    }
    if (picked.size > maxBytes) {
      setFile(null);
      setError(`That file is larger than the ${formatSize(maxBytes)} limit.`);
      return;
    }
    setFile(picked);
    // A heading is required, and the filename is the most likely one. It is only
    // ever a starting point: it fills an empty box and never overwrites typing.
    setHeading((prev) => (prev.trim() ? prev : picked.name.replace(/\.[^.]+$/, '')));
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!file || !heading.trim() || sending) return;
    setError(null);
    setSending(true);
    setFraction(0);

    const body = new FormData();
    body.append('kind', kind);
    body.append('heading', heading.trim());
    body.append('file', file);
    abort.current = new AbortController();

    try {
      await uploadWithProgress<{ document: BlueprintDocument }>(
        `/blueprints/${blueprintId}/documents`,
        body,
        setFraction,
        abort.current.signal,
      );
      await queryClient.invalidateQueries({ queryKey: documentKeys.list(blueprintId) });
      setSending(false);
      setOpen(false);
      setFile(null);
      setHeading('');
      setFraction(0);
    } catch (err) {
      setSending(false);
      setError(
        err instanceof ApiError ? err.message : 'The upload did not complete. Nothing was added.',
      );
    } finally {
      abort.current = null;
    }
  };

  return (
    <>
      <button type="button" className="btn" onClick={() => setOpen(true)}>
        {label}
      </button>

      <Dialog open={open} title={label} onClose={close} dismissible={!sending}>
        <form onSubmit={submit} className="bp-dialog-form">
          <div className="field">
            <label className="field-label" htmlFor="doc-heading">
              Heading
            </label>
            <input
              id="doc-heading"
              value={heading}
              maxLength={HEADING_MAX}
              onChange={(e) => setHeading(e.target.value)}
              disabled={sending}
              required
            />
          </div>

          <div className="field">
            <span className="field-label">
              {picture
                ? `Picture (PNG, JPEG, WebP, GIF, BMP or SVG, up to ${formatSize(maxBytes)})`
                : `File (up to ${formatSize(maxBytes)})`}
            </span>
            {/* theme.css hides every file input, as the generator drives its own
                through a button. So does this. */}
            <div className="bp-file-row">
              <button
                type="button"
                className="btn"
                onClick={() => fileInput.current?.click()}
                disabled={sending}
              >
                {file ? 'Choose a different file' : 'Choose file'}
              </button>
              <span className="bp-file-name" title={file?.name}>
                {file ? `${file.name} · ${formatSize(file.size)}` : 'No file chosen'}
              </span>
            </div>
            <input
              ref={fileInput}
              type="file"
              {...(picture ? { accept: PICTURE_MIME.join(',') } : {})}
              tabIndex={-1}
              aria-hidden="true"
              onChange={(e) => {
                choose(e.target.files?.[0] ?? null);
                // Allow choosing the same file again after a rejected attempt.
                e.target.value = '';
              }}
            />
          </div>

          {sending ? (
            <progress value={fraction} max={1} aria-label="Upload progress" className="bp-progress" />
          ) : null}

          {error ? (
            <p className="bp-inline-err" role="alert">
              {error}
            </p>
          ) : null}

          <div className="bp-dialog-actions">
            <button type="button" className="btn" onClick={close} disabled={sending}>
              Cancel
            </button>
            <button
              type="submit"
              className="btn primary"
              disabled={!file || !heading.trim() || sending}
            >
              {sending ? 'Uploading…' : 'Upload'}
            </button>
          </div>
        </form>
      </Dialog>
    </>
  );
}
